import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useCustomTimeout } from 'owa-react-hooks/lib/useCustomTimeout';
import type FocusableSurface from '../interfaces/FocusableSurface';
import FocusManagedComponent from './FocusManagedComponent';
import registerTabbableChildren from '../utils/registerTabbableChildren';
import { SurfaceFocusManager } from '../utils/SurfaceFocusManager';

/**
 * Props for focusable surface
 */
export interface FocusableSurfaceComponentProps {
    setFocusedElement: (id: string) => void;
    tabOrderProvider: () => string[];
    initialTabId?: string;
    children?: React.ReactNode;
}

/**
 * The component that maintains the focus manager and has public api for interacting with the focusmanager.
 * Registers all tabbable elements that have the tabid property on them, captures tabs and clicks to manage focus
 * within the component
 */
export default observer(
    function FocusableSurfaceComponent(
        props: FocusableSurfaceComponentProps,
        ref: React.Ref<FocusableSurface>
    ) {
        const focusManager = React.useRef<SurfaceFocusManager>(
            new SurfaceFocusManager(props.setFocusedElement)
        );
        const rootElement = React.useRef<HTMLElement>();
        const [setFocusTimer] = useCustomTimeout();

        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * Sets focus to the actual focused element
                 */
                focusSurface() {
                    focusManager.current.focusOnFocusedElement();
                },
                /**
                 * Sets focus to a particular item
                 * @param id tabId of the item to focus
                 */
                focusItem(id: string) {
                    setFocusTimer(
                        focusManager.current.focusOnItem.bind(focusManager.current),
                        0,
                        id
                    );
                },
                /**
                 * Checks whether the surface currently has focus
                 */
                surfaceHasFocus() {
                    return (
                        rootElement.current &&
                        document.activeElement &&
                        rootElement.current == document.activeElement
                    );
                },
            }),
            []
        );

        focusManager.current.defineOrder(props.tabOrderProvider());
        focusManager.current.setInitialFocusId(props.initialTabId);
        let surface = (
            <div
                tabIndex={-1}
                ref={el => {
                    rootElement.current = el;
                    focusManager.current.registerRootElementReference(el);
                }}>
                {props.children}
            </div>
        );
        surface = registerTabbableChildren(surface, focusManager.current);
        return (
            <FocusManagedComponent key={surface.key} focusManager={focusManager.current}>
                {surface}
            </FocusManagedComponent>
        );
    },
    { forwardRef: true }
);
